import { useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import {
	type TPickerIntroBeat,
	type TPickerIntroMotion,
	initialPickerIntroBeat,
	nextPickerIntroBeat,
	pickerIntroDelayAfter,
} from "./picker-intro";

/**
 * Advances the picker intro on a clock, not scroll. Plays once per page session;
 * dismissing a flow or a deep-link `?path=` leaves the last beat in place.
 */
export function usePickerIntro(play: boolean): { beat: TPickerIntroBeat; motion: TPickerIntroMotion } {
	const reducedMotion = useReducedMotion();
	const skipMotion = !play || !!reducedMotion;
	const playedRef = useRef(skipMotion);
	const [beat, setBeat] = useState<TPickerIntroBeat>(() => initialPickerIntroBeat(skipMotion ? "skip" : "play"));
	const [motion, setMotion] = useState<TPickerIntroMotion>(() => (skipMotion ? "skip" : "play"));

	useEffect(() => {
		if (skipMotion || playedRef.current) {
			playedRef.current = true;
			setBeat("choices");
			setMotion("skip");
			return;
		}

		setBeat("robot");
		setMotion("play");
		let current: TPickerIntroBeat = "robot";
		let timer = 0;
		const advance = () => {
			const next = nextPickerIntroBeat(current);
			if (!next) {
				playedRef.current = true;
				return;
			}
			current = next;
			setBeat(next);
			if (next === "choices") {
				playedRef.current = true;
				return;
			}
			timer = window.setTimeout(advance, pickerIntroDelayAfter(next));
		};
		timer = window.setTimeout(advance, pickerIntroDelayAfter("robot"));

		return () => window.clearTimeout(timer);
	}, [skipMotion]);

	return { beat, motion };
}
